import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Select as MultiSelect } from 'chakra-react-select';
import { CourtDto } from '../../../models/court/dto/court.dto';
import { VenueAgentDto } from '../../../models/court-recording/dto/venue-agent.dto';
import { typography } from '../../../theme/tokens';
import { useUpdateVenueAgentProvisionedCourts } from '../hooks/useUpdateVenueAgentProvisionedCourts';
import { vt } from '../utils/venue-agent.utils';

interface CourtOption {
  label: string;
  value: number;
}

interface Props {
  agent: VenueAgentDto;
  organizationId: string;
  courts: CourtDto[];
  isLoadingCourts?: boolean;
}

const sortedIds = (ids: number[] | undefined | null): number[] =>
  [...(ids ?? [])].sort((a, b) => a - b);

const sameScope = (a: number[], b: number[]): boolean =>
  a.length === b.length && a.every((id, index) => id === b[index]);

/**
 * Admin control over `provisionedCourts` — the ONLY tamper-proof scope the API
 * authorises agent recording calls against.
 *
 * The empty selection is the dangerous case and is deliberately not treated as
 * a neutral "clear": the API reads an empty array as "no admin-enforced
 * restriction" and falls back to FULL-ORG access, so emptying this field
 * WIDENS the agent's reach rather than disabling it. The submit control
 * changes label and colour in that state so nobody clears a scope thinking
 * they are locking a box down.
 */
export default function VenueAgentProvisionedCourtsControl({
  agent,
  organizationId,
  courts,
  isLoadingCourts = false,
}: Props) {
  const toast = useToast();
  const mutation = useUpdateVenueAgentProvisionedCourts(organizationId);

  const savedScope = useMemo(
    () => sortedIds(agent.provisionedCourts),
    [agent.provisionedCourts],
  );
  const [selected, setSelected] = useState<number[]>(savedScope);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Re-sync when the agents query refetches with a different saved scope (our
  // own PATCH, or another admin's). Keyed on the serialised ids so an
  // identical array with a new identity doesn't stomp an in-progress edit.
  const savedScopeKey = savedScope.join(',');
  useEffect(() => {
    setSelected(savedScope);
    setErrorMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedScopeKey]);

  const options: CourtOption[] = useMemo(
    () => courts.map((court) => ({ label: court.name, value: court.id })),
    [courts],
  );

  const selectedOptions: CourtOption[] = useMemo(
    () =>
      selected.map(
        (id) =>
          options.find((option) => option.value === id) ?? {
            // A court that was provisioned and has since been deleted (or that
            // lives in a location this admin can't list) still has to be
            // visible — otherwise saving would silently drop it.
            label: vt('unknown_court', { id }),
            value: id,
          },
      ),
    [selected, options],
  );

  // Compared and submitted in a canonical order. The option list is sorted by
  // court NAME, so a selection can come back in an order that differs from the
  // ascending ids we store — without normalising, re-picking the exact same
  // courts would light up "Save" and PATCH a pointless reorder.
  const normalizedSelection = useMemo(() => sortedIds(selected), [selected]);
  const isDirty = !sameScope(normalizedSelection, savedScope);
  const willClearScope = normalizedSelection.length === 0;
  const hadScope = savedScope.length > 0;

  const handleSave = async (): Promise<void> => {
    setErrorMessage(null);
    try {
      await mutation.mutateAsync({ agentId: agent.id, courts: normalizedSelection });
      toast({
        title: willClearScope ? vt('scope_cleared_toast') : vt('scope_updated'),
        status: willClearScope ? 'warning' : 'success',
        duration: 3000,
      });
    } catch (err) {
      // Server-side validation (unknown / other-org / deleted court ids) is
      // authoritative; show exactly what it said rather than paraphrasing.
      const message = err instanceof Error ? err.message : String(err);
      setErrorMessage(message);
      toast({
        title: vt('scope_update_failed'),
        description: message,
        status: 'error',
      });
    }
  };

  return (
    <FormControl>
      <FormLabel {...typography.formLabel} mb={2}>
        {vt('provisioned_courts')}
      </FormLabel>
      <MultiSelect
        isMulti
        isDisabled={Boolean(agent.revokedAt) || mutation.isLoading}
        isLoading={isLoadingCourts}
        options={options}
        value={selectedOptions}
        placeholder={vt('all_courts_placeholder')}
        aria-label={vt('provisioned_courts')}
        onChange={(next) =>
          setSelected((next as readonly CourtOption[] | null ?? []).map((o) => o.value))
        }
        size="sm"
      />
      <FormHelperText fontSize="xs">
        {vt('provisioned_helper')}
      </FormHelperText>

      {willClearScope && (
        <Alert status="warning" mt={3} borderRadius="md" fontSize="sm">
          <AlertIcon />
          <Box>
            <Text fontWeight="600">{vt('clear_warning_title')}</Text>
            <Text fontSize="xs">
              {vt('clear_warning_body_before')}{' '}
              <strong>{vt('clear_warning_body_strong')}</strong>
              {vt('clear_warning_body_after')}
            </Text>
          </Box>
        </Alert>
      )}

      {errorMessage && (
        <Alert status="error" mt={3} borderRadius="md" fontSize="sm">
          <AlertIcon />
          <Text>{errorMessage}</Text>
        </Alert>
      )}

      <Flex mt={3} gap={2} alignItems="center">
        <Button
          size="sm"
          colorScheme={willClearScope && hadScope ? 'red' : 'brand'}
          isDisabled={!isDirty || Boolean(agent.revokedAt)}
          isLoading={mutation.isLoading}
          onClick={() => void handleSave()}
        >
          {willClearScope && hadScope ? vt('clear_scope_button') : vt('save_scope')}
        </Button>
        {isDirty && (
          <Button
            size="sm"
            variant="ghost"
            isDisabled={mutation.isLoading}
            onClick={() => {
              setSelected(savedScope);
              setErrorMessage(null);
            }}
          >
            {vt('reset')}
          </Button>
        )}
      </Flex>
    </FormControl>
  );
}
